import { useMemo } from 'react'
import type { TmsInvoiceRow, TmsLoadsReadiness } from '@numeo/types-main-web-app'
import { InvoicingRowsTable } from '../invoicing/InvoicingTable'
import { rowKeyOf } from '../invoicing/invoicingRows'
import { amazonRowsOf } from './cardResults'
import { ReadinessView } from './ReadinessView'

/** The loads a readiness read answers as invoicing rows (ready, needs documents, invoiced), each load once, in that order. */
export const invoicingRowsOfReadiness = (readiness: TmsLoadsReadiness): TmsInvoiceRow[] => {
    const seen = new Set<string>()
    return [...readiness.ready, ...readiness.needs_documents, ...(readiness.invoiced?.rows ?? [])].filter((row) => {
        const key = rowKeyOf(row)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

/** What a readiness read holds besides invoicing rows: Amazon-billed loads, loads on the road, numbers no load or several match. */
export const readinessRestOf = (readiness: TmsLoadsReadiness): TmsLoadsReadiness | null => {
    const amazon = amazonRowsOf(readiness.billed_through_amazon)
    const rest = readiness.not_delivered.length + readiness.not_found.length + readiness.ambiguous.length + amazon.length
    if (!rest) return null
    return { ...readiness, ready: [], needs_documents: [], invoiced: { count: 0, amount_usd: null, rows: [] } }
}

interface ReadinessAnswerProps {
    cardId: string
    readiness: TmsLoadsReadiness
    question?: string
    onSend: (message: string) => void
}

/**
 * The loads a user named (`tms_invoices.worklist` with load_numbers), as v17 shows them: their invoicing rows in the in-chat table
 * (status, documents, amount, Review / View draft and the drawer), then what is not an invoicing row (on the road, Amazon-billed,
 * not found or ambiguous) in its own groups below.
 */
export const ReadinessAnswer = ({ cardId, readiness, question, onSend }: ReadinessAnswerProps) => {
    const rows = useMemo(() => invoicingRowsOfReadiness(readiness), [readiness])
    const rest = useMemo(() => readinessRestOf(readiness), [readiness])
    return (
        <div className="flex flex-col gap-3">
            {rows.length > 0 && <InvoicingRowsTable messageId={cardId} rows={rows} question={question} />}
            {rest && <ReadinessView readiness={rest} onSend={onSend} />}
        </div>
    )
}
